import { AppLayout, Button, Header, HelpPanel, Tabs } from '@amzn/awsui-components-react';
import { useLocalizedText } from '../hooks/localizedText';
import { AmbassadorDirectoryTable } from '../components/ambassadors/ambassadorDirectoryTable';
import { useEffect, useMemo, useState } from 'react';
import { AmbassadorDirectoryDosAndDonts } from '../components/ambassadors/ambassadorDirectoryDosAndDonts';
import { featureIsEnabled, useFeatures } from '../hooks/features';
import { COMMUNITY_AMBASSADOR_FEATURE_FLAG, NAVIGATION_REDESIGN } from '../utils/constants';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useGetAmbassadors } from '../hooks/ambassadors/getAmbassadors';
import { useUser } from '../hooks/user';
import { AmbassadorRecord } from '@amzn/red-velvet-api';
import { useCustomSearchParams } from '../hooks/useCustomSearchParams';
import { AppOutletContext } from '../App';

export function AmbassadorDirectory() {
  const [dosAndDontsOpen, setDosAndDontsOpen] = useState(false);
  const { t } = useLocalizedText(['ambassadors', 'translation']);
  const ambassadors = useGetAmbassadors();
  const loggedInUser = useUser();
  const navigate = useNavigate();
  const { searchParams, addSearchParam, deleteSearchParam } = useCustomSearchParams();
  const [urlSearchParams] = useSearchParams();
  const features = useFeatures();
  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, urlSearchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();

  const [activeTabId, setActiveTabId] = useState(
    searchParams.get('in-your-area') === 'true' ? 'area' : 'global'
  );

  const toolsPanel = (
    <HelpPanel header={<h2>{t('ambassadorDirectory.dosAndDonts')}</h2>}>
      <AmbassadorDirectoryDosAndDonts />
    </HelpPanel>
  );

  // Clean up tools panel on unmount in new nav mode
  useEffect(() => {
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, dosAndDontsOpen]);

  const registerOrOffboardButton = useMemo(() => {
    const currentUserIsAnAmbassador = ambassadors.data?.find(
      (item: AmbassadorRecord) => item.employeeId === loggedInUser.employeeId
    );
    if (!currentUserIsAnAmbassador) {
      return (
        <Button onClick={() => navigate('/ambassadors/register')}>
          {t('ambassadorDirectory.becomeAnAmbassador')}
        </Button>
      );
    } else {
      return (
        <Button
          data-testid="ambassador-directory-update-button"
          onClick={() => addSearchParam('update', 'true')}
        >
          {t('ambassadorDirectory.manageDetails')}
        </Button>
      );
    }
  }, [ambassadors.data, loggedInUser.employeeId, t, navigate, addSearchParam]);

  const content = (
    <div>
      <Header
        variant="h1"
        info={
          <Button
            data-directory-dos-and-donts
            onClick={() => {
              if (isNewNav && outletContext) {
                // One-way open: root AppLayout's X button handles closing.
                // Avoids toggle desync where local state disagrees with AppLayout's toolsOpen.
                outletContext.setTools(toolsPanel);
                outletContext.setToolsOpen(true);
              } else {
                setDosAndDontsOpen((d) => !d);
              }
            }}
            variant="inline-link"
          >
            {t('ambassadorDirectory.dosAndDonts')}
          </Button>
        }
        actions={<>{!ambassadors.isLoading && registerOrOffboardButton}</>}
        data-testid="ambassador-directory-header"
      >
        {t('ambassadorDirectory.title')}
      </Header>
      <Tabs
        onChange={({ detail }) => {
          if (detail.activeTabId === 'global') {
            deleteSearchParam('in-your-area');
          } else {
            addSearchParam('in-your-area', 'true');
          }
          setActiveTabId(detail.activeTabId);
        }}
        activeTabId={activeTabId}
        tabs={[
          {
            label: t('ambassadorDirectory.global'),
            id: 'global',
            content: (
              <AmbassadorDirectoryTable
                ambassadors={ambassadors.data || []}
                isLoading={ambassadors.isLoading}
              />
            ),
          },
          {
            label: t('ambassadorDirectory.inYourArea'),
            id: 'area',
            content: (
              <>
                <AmbassadorDirectoryTable
                  ambassadors={ambassadors.data || []}
                  isLoading={ambassadors.isLoading}
                  inYourArea
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );

  // New navigation: return content only, shell handled by root AppLayout
  if (isNewNav) {
    return content;
  }

  // Legacy: keep existing AppLayout wrapper
  return (
    <AppLayout
      navigationHide
      toolsHide={!dosAndDontsOpen}
      toolsOpen={dosAndDontsOpen}
      onToolsChange={() => setDosAndDontsOpen((d) => !d)}
      tools={toolsPanel}
      content={content}
    />
  );
}

export function AmbassadorDirectoryView() {
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  if (featureIsEnabled(COMMUNITY_AMBASSADOR_FEATURE_FLAG, features, searchParams)) {
    return <AmbassadorDirectory />;
  } else {
    return <></>;
  }
}
